import { useState, useEffect } from 'react';
import { FileText, AlertCircle, Archive, Loader2 } from 'lucide-react';
import axios from 'axios';
import { getApiBase } from './apiBase';

const API_BASE = getApiBase();
const API = axios.create({ baseURL: API_BASE, timeout: 15000, withCredentials: true });

const FOLDER_CONFIG = {
  logs: { label: 'Logs', icon: FileText, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
  error: { label: 'Errors', icon: AlertCircle, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
  archive: { label: 'Archive', icon: Archive, color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200' },
};

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleString();
}

function formatShortDate(d) {
  if (!d) return '';
  const date = new Date(d);
  const m = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return `${m}/${day}/${year}`;
}

function displayLogName(run) {
  const baseName = run.reportName || run.reportPath?.split('/').pop() || run.reportPath || 'Report';
  const ts = formatShortDate(run.createdAt);
  return ts ? `${baseName}_${ts}` : baseName;
}

export default function ReportHistory({ folder, refreshTrigger = 0 }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRuns = () => {
    if (!folder) return;
    setLoading(true);
    setError(null);
    API.get('/api/report-runs', { params: { folder } })
      .then((res) => setRuns(res.data.runs || []))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRuns();
  }, [folder, refreshTrigger]);

  const handleArchive = async (id) => {
    try {
      await API.patch(`/api/report-runs/${id}`, { folder: 'archive' });
      setRuns((prev) => prev.filter((r) => r._id !== id));
    } catch (err) {
      console.error('Archive failed:', err);
    }
  };

  const config = FOLDER_CONFIG[folder] || FOLDER_CONFIG.logs;
  const Icon = config.icon;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg ${config.bg} ${config.border} border p-4 text-sm ${config.color}`}>
        <AlertCircle className="h-5 w-5 inline-block mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${config.color}`} />
          <h2 className="text-lg font-semibold text-slate-800">{config.label}</h2>
          <span className="text-sm text-slate-500">({runs.length} runs)</span>
        </div>
        <button
          type="button"
          onClick={() => fetchRuns()}
          disabled={loading}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {runs.length === 0 ? (
        <div className="card-panel p-10 text-center text-slate-500">
          No report runs in {config.label.toLowerCase()} yet.
        </div>
      ) : (
        <div className="space-y-2">
          {runs.map((run) => (
            <div
              key={run._id}
              className={`card-panel p-4 flex items-center justify-between gap-4 ${config.border} border`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-800 truncate">
                  {displayLogName(run)}
                </p>
                <p className="text-xs text-slate-500 truncate mt-0.5">{run.reportPath}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {formatDate(run.createdAt)} · {run.runType === 'upload' ? 'Upload' : run.format} · {run.status}
                  {run.username && ` · ${run.username}`}
                </p>
                {run.errorMessage && (
                  <p className="text-xs text-red-600 mt-1 truncate" title={run.errorMessage}>
                    {run.errorMessage}
                  </p>
                )}
              </div>
              {folder === 'logs' && (
                <button
                  type="button"
                  onClick={() => handleArchive(run._id)}
                  className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Archive
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
